import type { StandingsResponse } from '@league/shared/contract';
import { DIRECTOR_LIFECYCLE_SESSION_DURATION_MS } from '@league/shared/league-lifecycle';
import { useEffect, useReducer } from 'react';
import { rosterLockState } from '@league/shared/roster-lock';
import { KIT_STATUS, type KitStatusKey } from '@league/shared/status';
import { Icon, LINK_CLASS, StatusChip, buttonClass } from '@league/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useOutletContext } from 'react-router-dom';

import { leagueDate, leagueTime } from '../../../lib/when';
import { apiFetch } from '../../../lib/api';
import type { LeagueContext } from '../LeagueWorkspace';
import { SessionRows } from '../SessionRows';
import { useRunsOnlyNights } from '../../../lib/capabilities';
import { KIT_IN_V1, ROSTER_LOCK_IN_V1 } from '@league/shared/features';

/** Only what these two panels read. The full row lives in the kit screens. */
interface KitRow {
  id: string;
  kind: string;
  status: string;
  leagueId: string | null;
}

/** A league’s live pulse, with its existing historical view retained after close-out. */
export function OverviewTab() {
  const { league, sessions, lifecycle } = useOutletContext<LeagueContext>();

  /*
    AN INSTRUCTOR'S OVERVIEW (2026-10-16). The roster is the director's, so the player count comes
    from the league read instead, the waitlist is not shown, and close-out is not offered.
  */
  const nightsOnly = useRunsOnlyNights();
  const roster = useQuery({
    queryKey: ['roster', league.id],
    enabled: !nightsOnly,
    queryFn: () =>
      apiFetch<{
        roster: { enrollment: { status: string } }[];
        rosterLockAt: string | null;
      }>(`/api/leagues/${league.id}/roster`),
  });

  const showLeaders = lifecycle?.state === 'in_play' || lifecycle?.state === 'finished';
  const standings = useQuery({
    queryKey: ['standings', league.id],
    queryFn: () => apiFetch<StandingsResponse>(`/api/leagues/${league.id}/standings`),
    enabled: showLeaders,
  });
  const [, refreshClock] = useReducer((value: number) => value + 1, 0);
  const now = Date.now();
  const scheduled = sessions.filter((session) => session.status !== 'voided')
    .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const boundaries = scheduled.flatMap((session) => [
    Date.parse(session.startsAt), Date.parse(session.startsAt) + DIRECTOR_LIFECYCLE_SESSION_DURATION_MS,
  ]).filter((time) => time > now);
  const nextBoundary = boundaries.length ? Math.min(...boundaries) : null;

  // Intermediate session windows change even when the league remains In Play.
  useEffect(() => {
    if (nextBoundary === null || lifecycle?.state === 'completed') return;
    const timer = window.setTimeout(refreshClock, Math.max(1, Math.min(nextBoundary - now, 60_000)));
    return () => window.clearTimeout(timer);
  }, [nextBoundary, now, lifecycle?.state]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshClock(); };
    window.addEventListener('focus', refreshClock);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', refreshClock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const entries = roster.data?.roster ?? [];
  const seats = nightsOnly
    ? league.enrolledCount
    : entries.filter((e) => e.enrollment.status === 'enrolled').length;
  const countsMissing = !nightsOnly && (roster.isPending || roster.isError);
  const waiting = entries.filter((e) => e.enrollment.status === 'waitlist').length;
  const lock = rosterLockState(
    roster.data?.rosterLockAt ? new Date(roster.data.rosterLockAt) : null,
    new Date(),
  );

  /**
   * Only things that are actually waiting on her, and each says the consequence rather than the
   * count. An empty list is a real and good state — "nothing needs you" beats an empty panel.
   */
  const needsYou = [
    ROSTER_LOCK_IN_V1 && lock.approaching
      ? {
          key: 'lock',
          text: `The roster locks in ${lock.daysUntil} day${lock.daysUntil === 1 ? '' : 's'}. HQ prints from exactly this list.`,
          to: 'roster',
        }
      : null,
  ].filter(Boolean) as { key: string; text: string; to: string }[];

  const nextSteps = (!nightsOnly && !roster.isPending && needsYou.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className={lifecycle?.state === 'in_play' ? "font-display text-xl text-ink" : "text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3"}>
            Next steps
          </h2>
          <ul className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
            {needsYou.map((item) => (
              <li key={item.key} className="px-5 py-3.5">
                <Link to={item.to} className={`text-sm ${LINK_CLASS}`}>
                  {item.text}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null);

  if (!lifecycle) return <p className="text-sm text-ink-3">Loading…</p>;

  if (lifecycle.state !== 'completed') {
    const beforePlay = lifecycle.state === 'rostering' || lifecycle.state === 'ready';
    const finished = lifecycle.state === 'finished';
    const inPlay = lifecycle.state === 'in_play';
    const current = scheduled.filter((session) => {
      const start = Date.parse(session.startsAt);
      return start <= now && now < start + DIRECTOR_LIFECYCLE_SESSION_DURATION_MS;
    }).at(-1);
    const upcoming = scheduled.find((session) => Date.parse(session.startsAt) > now);
    const selected = finished ? scheduled.at(-1) : beforePlay ? scheduled[0] : current ?? upcoming;
    const sessionHeading = finished ? 'Last Session' : beforePlay ? 'First Session'
      : current ? 'Current Session' : 'Next Session';
    const leaders = (standings.data?.standings ?? []).filter((row) => row.rank <= 3);
    const summary = (
      <section aria-label="League summary" className="grid grid-cols-2 gap-3">
        <Tile label="Players" value={countsMissing ? '—' : String(seats)} note={`of ${league.playerCap}`} />
        {nightsOnly ? null : <Tile label="Waitlist" value={countsMissing ? '—' : String(waiting)} />}
      </section>
    );

    return (
      <div className="flex flex-col gap-6">
        {finished && !nightsOnly ? (
          <section className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-light bg-paper p-6">
            <div>
              <h2 className="font-display text-xl text-ink">Ready to close out?</h2>
              <p className="mt-2 text-sm text-ink-2">Review your league results and complete close-out.</p>
            </div>
            <Link to="close-out" className={buttonClass('secondary')}>Open Close-out</Link>
          </section>
        ) : null}

        {/*
          ⚠ NOT `items-start` — the two columns match each other's height, owner 2026-09-19.

          With `items-start` each column was only as tall as its own content, so the navy panel
          stopped short of the two cards beside it and the row ended on a ragged edge. Navy is the
          GROUND on this platform, not a card, and a ground that stops halfway down reads as
          something that failed to load rather than as a panel.

          Stretch is the grid default, so this is the absence of an override rather than an override.
        */}
        <div className={inPlay ? "grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]" : "contents"}>
        {showLeaders ? (
          <section aria-labelledby="league-leaders" className={`rounded-md border px-5 py-4 ${inPlay ? 'min-w-0 border-deep bg-deep text-on-deep' : 'border-light bg-paper'}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h2 id="league-leaders" className={`font-display text-xl ${inPlay ? 'text-on-deep' : 'text-ink'}`}>League Leaders</h2>
              <Link to="standings" className={inPlay ? 'text-sm text-on-deep-2 underline decoration-on-deep-3 underline-offset-4 hover:text-on-deep' : `text-sm ${LINK_CLASS}`}>View Full Standings</Link>
            </div>
            {standings.isPending ? (
              <p className={`mt-5 text-sm ${inPlay ? 'text-on-deep-2' : 'text-ink-3'}`}>Loading league leaders…</p>
            ) : standings.isError ? (
              <p className={`mt-5 text-sm ${inPlay ? 'text-on-deep-2' : 'text-ink-2'}`}>League leaders could not be loaded. Please try again.</p>
            ) : leaders.length === 0 ? (
              <p className={`mt-5 text-sm ${inPlay ? 'text-on-deep-2' : 'text-ink-2'}`}>League leaders will appear here when standings results are available.</p>
            ) : (
              <ol className={`mt-4 divide-y ${inPlay ? 'divide-on-deep/15' : 'divide-light'}`}>
                {leaders.map((row) => (
                  <li key={row.playerId} className={`flex items-center ${inPlay ? 'gap-3 py-3' : 'gap-4 py-4'}`}>
                    {inPlay ? (
                      <span aria-label={`${row.tied ? 'Tied rank' : 'Rank'} ${row.rank}`} className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-deep ${row.rank === 1 ? 'bg-[#C8B780]' : row.rank === 2 ? 'bg-light' : 'bg-[#BE9E83]'}`}>
                        {row.rank}
                      </span>
                    ) : <span className="w-14 shrink-0 text-sm text-ink-3">{row.tied ? 'Tied ' : ''}{row.rank === 1 ? '1st' : row.rank === 2 ? '2nd' : '3rd'}</span>}
                    <span className={`min-w-0 flex-1 font-display text-lg leading-snug ${inPlay ? 'text-on-deep' : 'text-ink'}`}>{row.player.firstName} {row.player.lastName}</span>
                    <div className="shrink-0 text-right">
                      <p className={`tabular text-xl font-semibold ${inPlay ? 'text-on-deep' : 'text-ink'}`}>{row.points}</p>
                      <p className={`text-xs ${inPlay ? 'text-on-deep-3' : 'text-ink-3'}`}>points</p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        ) : null}

        {beforePlay ? summary : null}
        <div className={inPlay ? "flex min-w-0 flex-col gap-4" : "contents"}>
        {selected ? (
          <section className="rounded-md border border-light bg-paper px-5 py-4">
            <h2 className="flex items-center gap-2 font-display text-lg text-ink">
              {inPlay ? <Icon name="league" size={22} className="shrink-0 text-ink-3" /> : null}
              {inPlay && !current ? 'Up Next' : sessionHeading}
            </h2>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
              <div>
                {inPlay ? (
                  <>
                    <p className="text-sm text-ink-2">Session {selected.sessionNumber}</p>
                    <p className="mt-1 text-base font-medium text-ink">{leagueDate(selected.startsAt, league.timeZone, { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                  </>
                ) : <p className="text-base font-medium text-ink">Session {selected.sessionNumber} · {leagueDate(selected.startsAt, league.timeZone, { weekday: 'long', month: 'long', day: 'numeric' })}</p>}
                <p className="mt-1 text-sm text-ink-2">{leagueTime(selected.startsAt, league.timeZone)}</p>
                {league.venueName ? <p className={inPlay ? "mt-4 flex items-start gap-2 text-sm text-ink-2" : "mt-1 text-sm text-ink-2"}>
                  {inPlay ? <Icon name="market" size={20} className="shrink-0 text-ink-3" /> : null}
                  {inPlay ? (
                    <span>
                      <span className="block font-medium text-ink">{league.venueName.split(',')[0]?.trim()}</span>
                      {league.venueName.includes(',') && league.venueName.split(',').slice(1).join(',').trim() ? <span className="mt-0.5 block text-xs text-ink-2">{league.venueName.split(',').slice(1).join(',').trim()}</span> : null}
                    </span>
                  ) : league.venueName}
                </p> : null}
              </div>
              {beforePlay ? <Link to={`/director/sessions/${selected.id}/seating`} className={`text-sm ${LINK_CLASS}`}>View Seating</Link> : null}
            </div>
          </section>
        ) : (
          <p className="text-sm text-ink-2">{lifecycle.exception === 'all_sessions_voided' ? 'All sessions voided' : 'No sessions are scheduled yet.'}</p>
        )}
        {inPlay ? (
          <section className="rounded-md border border-light bg-paper px-5 py-4">
            <h2 className="flex items-center gap-2 font-display text-lg text-ink"><Icon name="players" size={22} className="shrink-0 text-ink-3" />League Snapshot</h2>
            <dl className="mt-3 grid grid-cols-2 gap-4">
              <div className="flex flex-col">
                <dt className="mt-1 text-xs text-ink-3">Players</dt>
                <dd className="tabular order-first font-display text-2xl text-ink">{countsMissing ? '—' : seats}</dd>
              </div>
              {nightsOnly ? null : (
                <div className="flex flex-col">
                  <dt className="mt-1 text-xs text-ink-3">Waitlist</dt>
                  <dd className="tabular order-first font-display text-2xl text-ink">{countsMissing ? '—' : waiting}</dd>
                </div>
              )}
            </dl>
          </section>
        ) : null}
        </div>
        </div>
        {!beforePlay && !inPlay ? summary : null}
        {/*
          THE WHOLE RUN, under the one session she is looking at. This page used to show a single
          card and nothing else while a league was live, so "when is week four, and did week two's
          scores ever go in" was a question the overview could not answer at all.
        */}
        <section className="flex flex-col gap-3">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
            The sessions
          </h2>
          <SessionRows
            sessions={sessions}
            timeZone={league.timeZone}
            venueName={league.venueName}
          />
        </section>
        {nextSteps}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {nextSteps}

      <section className={ROSTER_LOCK_IN_V1 ? 'grid gap-3 sm:grid-cols-3' : 'grid gap-3 sm:grid-cols-2'}>
        <Tile label="Players" value={`${seats} of ${league.playerCap}`} />
        {nightsOnly ? null : <Tile label="Waitlist" value={String(waiting)} />}
        {/* The lock is not part of V1 — see `@league/shared/features`. */}
        {ROSTER_LOCK_IN_V1 ? (
          <Tile
            label="Roster locks"
            value={lock.lockAt ? leagueDate(lock.lockAt, league.timeZone) : '—'}
            note={lock.locked ? 'Locked' : undefined}
          />
        ) : null}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
            The sessions
          </h2>
        </div>

        <SessionRows sessions={sessions} timeZone={league.timeZone} venueName={league.venueName} />
      </section>

      {/* Kit is not part of V1 — see `@league/shared/features`. */}
      {KIT_IN_V1 ? (
        <section className="grid gap-3">
          <KitPanel leagueId={league.id} />
        </section>
      ) : null}
    </div>
  );
}

function Tile({ label, value, note }: { label: string; value: string; note?: string | undefined }) {
  return (
    <div className="rounded-md border border-light bg-paper px-5 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
        {label}
      </p>
      <p className="tabular mt-1.5 font-display text-2xl leading-none">{value}</p>
      {note ? <p className="mt-1 text-[12px] text-ink-2">{note}</p> : null}
    </div>
  );
}

function Panel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-light bg-paper px-5 py-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">{label}</p>
      <div className="mt-2">{children}</div>
    </div>
  );
}

/**
 * WHERE THIS LEAGUE'S PACK IS.
 *
 * The kit list is national and already scoped; this filters to the one league whose page she is on.
 * The states come from the shared status vocabulary rather than being spelled here — the whole
 * point of that module is that "Delivered" is not also written "delivered" somewhere else.
 */
function KitPanel({ leagueId }: { leagueId: string }) {
  const kits = useQuery({
    queryKey: ['kits'],
    queryFn: () => apiFetch<{ kits: KitRow[] }>('/api/kits'),
  });

  const mine = (kits.data?.kits ?? []).filter((kit) => kit.leagueId === leagueId);

  return (
    <Panel label="Kit &amp; shipping">
      {kits.isPending ? (
        <p className="text-[13px] text-ink-2">Loading…</p>
      ) : mine.length === 0 ? (
        /* A real empty state: no pack has been made for this league yet, which is a fact not a gap. */
        <p className="text-[13px] leading-snug text-ink-2">
          Nothing has been printed for this league yet. HQ makes the pack once the roster locks.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {mine.map((kit) => {
            const status = KIT_STATUS[kit.status as KitStatusKey];
            return (
              <li key={kit.id} className="flex items-center justify-between gap-3">
                <span className="text-[13px] capitalize">{kit.kind}</span>
                {status ? <StatusChip status={status} /> : null}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
